import { useCallback, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp, Package } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/** Sub-line kinds a one-letter marker says at a glance; other kinds keep their text label. */
const LINE_KIND_MARKERS: Partial<Record<string, { glyph: string; label: string }>> = {
  informational: { glyph: 'i', label: 'Informational' },
  warranty: { glyph: 'W', label: 'Warranty' },
}

/**
 * A 16px (i)/(W) marker for informational and warranty lines, placed left of a sub-line's
 * chevron. Renders nothing for other kinds. The full word is its accessible name and tooltip.
 */
export function LineKindMarker({
  kind,
  className,
}: {
  kind: string | null | undefined
  className?: string
}) {
  const marker = kind ? LINE_KIND_MARKERS[kind] : undefined
  if (!marker) return null
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            tabIndex={0}
            aria-label={marker.label}
            data-line-kind-marker={kind}
            className={cn(
              'inline-flex size-4 shrink-0 cursor-default items-center justify-center rounded-full border border-current text-[10px] font-semibold leading-none text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              className,
            )}
          >
            <span aria-hidden="true">{marker.glyph}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={4}>
          {marker.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** The text kind pill, for the kinds `LineKindMarker` does not cover (fee, accessory, …). */
export function LineKindBadge({
  kind,
  label,
  className,
}: {
  kind: string | null | undefined
  label: ReactNode
  className?: string
}) {
  if (!kind || kind === 'main' || LINE_KIND_MARKERS[kind]) return null
  return (
    <Badge variant="outline" className={className}>
      {label}
    </Badge>
  )
}

function distinctSku(model: string, sku: string | null | undefined) {
  if (!sku) return null
  const normalize = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '')
  return normalize(model) && normalize(model) === normalize(sku) ? null : sku
}

/**
 * Rich product identity cell shared with the PO line-items table. It preserves
 * the PO detail layout: thumbnail slot, model link, description, and optional
 * manufacturer link/label.
 */
export function POLineItemProductCell({
  model,
  description,
  sku,
  specifications,
  productUuid,
  imageUrl,
  manufacturerName,
  manufacturerUuid,
  modelContent,
  modelSuffix,
  descriptionContent,
  showManufacturer = true,
  compact = false,
  skuBeforeDescription = false,
  inlineSku = false,
  collapseLongDescription = false,
  collapsedDescriptionLines = 2,
  imageClassName,
  className,
}: {
  model: string
  description?: string | null
  sku?: string | null
  specifications?: string | null
  productUuid?: string | null
  imageUrl?: string | null
  manufacturerName?: string | null
  manufacturerUuid?: string | null
  modelContent?: ReactNode
  /** Shown on the model's line, right of it (e.g. the item's status pill). */
  modelSuffix?: ReactNode
  descriptionContent?: ReactNode
  showManufacturer?: boolean
  compact?: boolean
  skuBeforeDescription?: boolean
  inlineSku?: boolean
  collapseLongDescription?: boolean
  /** Lines a collapsed description keeps; one line collapses anything longer than a short title. */
  collapsedDescriptionLines?: 1 | 2
  imageClassName?: string
  className?: string
}) {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  // Whether the clamped description actually hides text at the current width. A long string can
  // still fit on one wide row, and then the toggle would open onto nothing new.
  const [descriptionOverflows, setDescriptionOverflows] = useState(false)
  const visibleSku = distinctSku(model, sku)
  const descriptionText = description?.trim() || null
  const descriptionIsCollapsible =
    collapseLongDescription &&
    Boolean(
      descriptionText && descriptionText.length > (collapsedDescriptionLines === 1 ? 60 : 160),
    )
  const descriptionClamped = descriptionIsCollapsible && !descriptionExpanded
  const measureDescription = useCallback(
    (element: HTMLParagraphElement | null) => {
      if (!element || !descriptionClamped) return
      const measure = () => setDescriptionOverflows(element.scrollHeight > element.clientHeight + 1)
      measure()
      if (typeof ResizeObserver === 'undefined') return
      const observer = new ResizeObserver(measure)
      observer.observe(element)
      return () => observer.disconnect()
    },
    [descriptionClamped],
  )
  const showDescriptionToggle =
    descriptionIsCollapsible && (descriptionExpanded || descriptionOverflows)
  const titleClassName = compact
    ? 'block truncate text-sm font-medium text-primary hover:underline'
    : 'block break-words text-sm font-medium text-primary hover:underline'
  const plainTitleClassName = compact
    ? 'block truncate text-sm font-medium'
    : 'block break-words text-sm font-medium'
  const secondaryClassName = compact
    ? 'truncate text-xs text-muted-foreground'
    : 'whitespace-normal break-words text-xs text-muted-foreground'
  const specClassName = compact
    ? 'truncate text-xs text-muted-foreground'
    : 'whitespace-normal break-words text-xs text-muted-foreground'

  return (
    <div
      data-product-identity-cell="true"
      className={cn('flex min-w-0 max-w-full items-start gap-3', className)}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className={cn('h-10 w-10 shrink-0 rounded border object-cover', imageClassName)}
        />
      ) : (
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded border bg-muted',
            imageClassName,
          )}
        >
          <Package className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1 whitespace-normal">
        <div
          className={cn(
            (inlineSku || modelSuffix) && 'flex min-w-0 items-center gap-2',
            modelSuffix && 'flex-wrap gap-y-1',
          )}
        >
          <div className="min-w-0">
            {modelContent ? (
              modelContent
            ) : productUuid ? (
              <Link
                to="/products/$productId"
                params={{ productId: productUuid }}
                className={titleClassName}
              >
                {model}
              </Link>
            ) : (
              <span className={plainTitleClassName}>{model}</span>
            )}
          </div>
          {visibleSku && inlineSku ? (
            <span data-product-sku="true" className={cn(secondaryClassName, 'shrink-0')}>
              SKU: {visibleSku}
            </span>
          ) : null}
          {modelSuffix}
        </div>
        {visibleSku && skuBeforeDescription && !inlineSku ? (
          <p data-product-sku="true" className={secondaryClassName}>
            SKU: {visibleSku}
          </p>
        ) : null}
        {descriptionContent ? (
          <div className={secondaryClassName}>{descriptionContent}</div>
        ) : descriptionText ? (
          <>
            <p
              ref={measureDescription}
              className={cn(
                secondaryClassName,
                descriptionClamped &&
                  (collapsedDescriptionLines === 1 ? 'line-clamp-1' : 'line-clamp-2'),
              )}
            >
              {descriptionText}
            </p>
            {showDescriptionToggle ? (
              <button
                type="button"
                className="inline-flex min-h-11 items-center gap-1 text-xs font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 @3xl/po-lines:min-h-8"
                aria-expanded={descriptionExpanded}
                onClick={() => setDescriptionExpanded((expanded) => !expanded)}
              >
                {descriptionExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {descriptionExpanded ? 'Hide specifications' : 'Show specifications'}
              </button>
            ) : null}
          </>
        ) : null}
        {visibleSku && !skuBeforeDescription && !inlineSku ? (
          <p data-product-sku="true" className={secondaryClassName}>
            SKU: {visibleSku}
          </p>
        ) : null}
        {specifications && (
          <p data-product-specifications="true" className={specClassName} title={specifications}>
            {specifications}
          </p>
        )}
        {showManufacturer && manufacturerName && (
          <p className={secondaryClassName}>
            {manufacturerUuid ? (
              <Link
                to="/manufacturers/$manuId"
                params={{ manuId: manufacturerUuid }}
                className="whitespace-normal break-words hover:text-primary hover:underline"
              >
                {manufacturerName}
              </Link>
            ) : (
              manufacturerName
            )}
          </p>
        )}
      </div>
    </div>
  )
}
